import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, CloudArrowUpIcon, XMarkIcon,
  ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { waybillAPI } from '../../api/waybillAPI';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import Button from '../../components/Button';
import { currentMonthRange } from '../../utils/dateUtils';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-PH') : '—');
const PAGE_SIZE = 25;

const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function StatusBadge({ status }) {
  const map = {
    draft:     'bg-gray-100 text-gray-600',
    active:    'bg-blue-100 text-blue-700',
    delivered: 'bg-green-100 text-green-700',
    voided:    'bg-red-100 text-red-600 line-through',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Draft'}
    </span>
  );
}

function GLStatusDot({ status }) {
  if (status === 'posted')  return <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="GL Posted" />;
  if (status === 'failed')  return <span className="inline-block w-2 h-2 rounded-full bg-red-500"   title="GL Failed" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-yellow-400" title="GL Pending" />;
}

function BulkGLPostModal({ onClose, onDone }) {
  const now  = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const [dateTo,   setDateTo]   = useState(localDateStr());
  const [posting,  setPosting]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');

  const handlePost = async () => {
    if (!dateFrom || !dateTo) { setError('Both dates are required.'); return; }
    if (dateFrom > dateTo)    { setError('Start date must be before end date.'); return; }
    setError('');
    setPosting(true);
    try {
      const res = await waybillAPI.bulkGLPost({ dateFrom, dateTo, shipmentType: 'trucking' });
      setResult(res.data?.data || res.data);
      onDone();
    } catch (e) {
      setError(e?.response?.data?.message || 'Bulk GL post failed.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-5 w-5" />
        </button>

        <h2 className="text-base font-semibold text-gray-800 mb-1">Bulk Post to GL</h2>
        <p className="text-sm text-gray-500 mb-5">
          Posts all pending / failed trucking waybills in the selected date range to the General Ledger.
        </p>

        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                <input
                  type="date" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                <input
                  type="date" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {error && <p className="text-red-600 text-xs mb-3">{error}</p>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handlePost}
                disabled={posting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CloudArrowUpIcon className="h-4 w-4" />
                {posting ? 'Posting…' : 'Post to GL'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2 mb-5">
              <div className="flex justify-between text-sm border-b pb-2">
                <span className="text-gray-500">Posted</span>
                <span className="font-semibold text-green-700">{result.posted}</span>
              </div>
              <div className="flex justify-between text-sm border-b pb-2">
                <span className="text-gray-500">Failed</span>
                <span className="font-semibold text-red-600">{result.failed}</span>
              </div>
              <div className="flex justify-between text-sm pb-2">
                <span className="text-gray-500">Skipped (zero amount / already posted)</span>
                <span className="font-semibold text-gray-600">{result.skipped}</span>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
                <p className="text-xs font-semibold text-red-700 mb-1">Errors</p>
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600">{e.serial || e.waybillId}: {e.error}</p>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TruckingWaybillListPage() {
  const navigate = useNavigate();
  const { accountingClientId, contextKind } = useCompanyContext();
  const { first, last } = currentMonthRange();
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showBulkGL, setShowBulkGL] = useState(false);
  const [dateFrom, setDateFrom] = useState(first);
  const [dateTo, setDateTo] = useState(last);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchWaybills(dateFrom, dateTo);
  }, [accountingClientId, contextKind]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWaybills = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      const params = { shipmentType: 'trucking' };
      if (from) params.dateFrom = from;
      if (to)   params.dateTo   = to;
      const res = await waybillAPI.getAll(params);
      setWaybills(res.data || []);
      setPage(1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this waybill?')) return;
    try {
      await waybillAPI.delete(id);
      setWaybills((prev) => prev.filter((w) => w._id !== id));
    } catch (e) {
      alert('Failed to delete waybill.');
    }
  };

  const filtered = waybills.filter((w) => {
    if (statusFilter && w.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const customerName = (
      w.CustomerId?.CustomerName ||
      w.CustomerId?.name         ||
      w.CustomerName             || ''
    ).toLowerCase();
    return (
      w.SerialNo?.toLowerCase().includes(q)         ||
      w.referenceNumber?.toLowerCase().includes(q)  ||
      w.Shipper?.toLowerCase().includes(q)           ||
      customerName.includes(q)                       ||
      w.Origin?.toLowerCase().includes(q)            ||
      w.Destination?.toLowerCase().includes(q)       ||
      w.PlateNo?.toLowerCase().includes(q)           ||
      w.VanNo?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const pendingGLCount = waybills.filter(
    (w) => !w.isVoided && w.glPostingStatus !== 'posted'
  ).length;

  return (
    <Layout pageTitle="Waybill — Trucking">
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative w-80">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search serial, ref no., plate, consignee…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All Statuses</option>
            {['draft', 'active', 'delivered', 'voided'].map((s) => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <input
            type="date"
            aria-label="Date from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-ink-line rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="date"
            aria-label="Date to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-ink-line rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <Button onClick={() => fetchWaybills(dateFrom, dateTo)} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // Drops the date range too, so users can search a ref no. without knowing its date.
              setDateFrom(''); setDateTo(''); setStatusFilter(''); setSearch('');
              fetchWaybills('', '');
            }}
          >
            Clear
          </Button>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setShowBulkGL(true)}
              className="flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-700 text-sm rounded-lg hover:bg-teal-50 transition-colors"
            >
              <CloudArrowUpIcon className="h-4 w-4" />
              Post to GL
              {pendingGLCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-yellow-400 text-yellow-900">
                  {pendingGLCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/billing/trucking-waybill/add')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Add New Waybill
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['GL', 'Serial No.', 'Ref No.', 'Date', 'Shipper', 'Consignee', 'Origin', 'Destination', 'Plate No.', 'Van No.', 'Delivered', 'Status', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 13 }).map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-gray-400">
                    {search || statusFilter ? 'No matching waybills found.' : 'No trucking waybills yet.'}
                  </td>
                </tr>
              ) : (
                paginated.map((w) => {
                  const customer =
                    w.CustomerId?.CustomerName ||
                    w.CustomerId?.name         ||
                    w.CustomerName             || '—';
                  return (
                    <tr key={w._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <GLStatusDot status={w.glPostingStatus} />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{w.SerialNo || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{w.referenceNumber || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmt(w.TransactionDate)}</td>
                      <td className="px-4 py-3">{w.Shipper || '—'}</td>
                      <td className="px-4 py-3">{customer}</td>
                      <td className="px-4 py-3">{w.Origin || '—'}</td>
                      <td className="px-4 py-3">{w.Destination || '—'}</td>
                      <td className="px-4 py-3 font-medium">{w.PlateNo || '—'}</td>
                      <td className="px-4 py-3">{w.VanNo || '—'}</td>
                      <td className="px-4 py-3">
                        {w.IsDelivered ? (
                          <span className="text-green-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={w.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/billing/trucking-waybill/${w._id}/edit`)}
                            className="p-1.5 text-gray-400 hover:text-teal-600 rounded hover:bg-teal-50"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(w._id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-xs text-ink-body">
          <p>
            {filtered.length} waybill{filtered.length !== 1 ? 's' : ''} found
            {pendingGLCount > 0 && (
              <span className="ml-2 text-warning-600">· {pendingGLCount} pending GL post</span>
            )}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-label="Previous page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </Button>
              <span className="px-2">Page {safePage} of {totalPages}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Next page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {showBulkGL && (
        <BulkGLPostModal
          onClose={() => setShowBulkGL(false)}
          onDone={() => fetchWaybills()}
        />
      )}
    </Layout>
  );
}
